/**
 * VideoPlayer Component
 * 
 * Refactored to use the MediaEngine abstraction layer.
 * - Web: Uses Vidstack via WebMediaEngine
 * - Mobile (Phase 2): Will use NativeMediaEngine (@capgo/native-audio)
 * 
 * Supports:
 * - HLS streaming from Vimeo
 * - Akademia source URLs
 * - Offline playback from local files
 * - Lock screen / notification controls
 */

import { getAddOwnProduct, getVimeoVideoLink } from "@/api/api";
import type { VimeoResponse } from "@/api/vimeo.types";
import { cn } from "@/components/utils/utils";
import { useCourseProgress, useVideoProgress } from "@/context/StorageContext";
import { useUnifiedMediaSession } from "@/hooks/useUnifiedMediaSession";
import { useQuery } from "@tanstack/react-query";
import type { MediaPlayerInstance, MediaPlayEvent } from "@vidstack/react";
// eslint-disable-next-line no-duplicate-imports
import { MediaPlayer, MediaProvider } from "@vidstack/react";
import { useRef, useCallback, useEffect, useState } from "react";
import { waitForDuration } from "./MultimediaCard.utils";
import { generateVideoId, updateLastAccessed } from "@/utils/storage/offlineVideosStorage";
import { isNativePlatform } from "@/utils/storage/storage";
import { getLocalVideoUri } from "@/utils/videoDownloadService";
import type { ProductDataMultimedia } from "./types";
import * as Sentry from "@sentry/capacitor";
import { Toast } from "@capacitor/toast";
import {
	useMediaEngine,
	createHlsSource,
	createLocalSource,
	createMediaSource,
	type MediaEngineCallbacks,
} from "@/utils/mediaEngine";
import { useAudioSession } from "@/hooks/useAudioSession";

export const VideoPlayer = ({
	videoContent,
	open,
	productData,
	courseClassesId,
	setOpen,
}: {
	videoContent:
		| { video_source: "vimeo_source"; vimeoId: number }
		| { video_source: "akademia_source"; videoUrl: string }
		| { video_source: "local_memory" }
		| { video_source: "unknown" };
	open: "AUDIO" | "VIDEO" | false;
	productData: ProductDataMultimedia;
	courseClassesId: string;
	setOpen: (open: "AUDIO" | "VIDEO" | false) => void;
}): JSX.Element => {
	const { productSlug = "", productId = "", productType = "unknown" } = productData || {};

	useEffect(() => {
		if (!productData || !productId || !productType) {
			console.warn("[VideoPlayer] Missing product data", { productData, productId, productType });
		}
	}, [productData, productId, productType]);

	useEffect(() => {
		// Debug: Check Media Session API availability
		// console.log("🔍 Debug Info:", {
		// 	"Media Session API": !!navigator.mediaSession,
		// 	"User Agent": navigator.userAgent,
		// 	"Platform": navigator.platform
		// });

		// if (navigator.mediaSession) {
		// 	console.log("✅ Media Session API is supported");
		// } else {
		// 	console.warn("❌ Media Session API is NOT supported");
		// }
	}, []);

	const { markAsCompleted } = useCourseProgress();
	const vidPlayerRef = useRef<MediaPlayerInstance>(null);

	const {
		saveVideoProgress,
		shouldResumeFromSavedPosition,
		clearVideoProgress,
	} = useVideoProgress();

	// State for error handling
	const [videoError, setVideoError] = useState<string | null>(null);
	const [localVideoBlobUrl, setLocalVideoBlobUrl] = useState<string | null>(null);

	// ============================================
	// Media Engine Setup (Phase 1: WebMediaEngine)
	// ============================================

	const mediaEngineCallbacks: MediaEngineCallbacks = {
		onStateChange: (state) => {
			console.log("[VideoPlayer] Engine state changed:", state);
		},
		onTimeUpdate: (position) => {
			if (position.duration > 0) {
				void saveVideoProgress(productId, courseClassesId, position.currentTime, position.duration);
			}
		},
		onEnded: () => {
			void clearVideoProgress(productId, courseClassesId);
		},
		onError: (error) => {
			console.error("[VideoPlayer] Engine error:", error);
			setVideoError("Nie udało się załadować wideo. Spróbuj ponownie.");

			Sentry.captureException(error, {
				tags: { component: 'VideoPlayer' },
				contexts: {
					video: {
						source: videoContent.video_source,
						productId,
						courseClassesId
					}
				}
			});

			void Toast.show({
				text: "Błąd ładowania wideo",
				duration: "long",
				position: "bottom"
			});
		},
	};

	const {
		attachPlayer,
		handleCanPlay,
		handlePlay: engineHandlePlay,
		handlePause: engineHandlePause,
		handleEnded: engineHandleEnded,
		handleError: engineHandleError,
		handleTimeUpdate: engineHandleTimeUpdate,
		platform,
	} = useMediaEngine(
		{
			mediaType: "video",
			backgroundMode: true,
			showNotification: true,
		},
		mediaEngineCallbacks
	);

	// ============================================
	// Unified Media Session (lock screen controls)
	// ============================================

	const { updatePlaybackState, updatePositionState } = useUnifiedMediaSession(
		vidPlayerRef,
		{
			title: productSlug || "LTCA Video",
			artist: "Akademia LTCA",
			album: "Kursy LTCA",
		},
		open === "VIDEO"
	);

	// Attach player to engine when available
	useEffect(() => {
		if (open === "VIDEO" && vidPlayerRef.current) {
			attachPlayer(vidPlayerRef.current);
		}
	}, [open, attachPlayer]);

	const { refetch: addOwnProduct } = useQuery<unknown>({
		queryKey: ["addOwnProduct", productSlug],
		queryFn: () => getAddOwnProduct(productSlug),
		enabled: false,
	});

	const isVimeo = videoContent.video_source === "vimeo_source";
	const vimeoId = isVimeo ? videoContent.vimeoId : undefined;

	const { data: vimeoVideoLink } = useQuery<VimeoResponse | undefined>({
		queryKey: ["vimeoVideoLink", vimeoId],
		queryFn: () => getVimeoVideoLink(vimeoId!.toString()),
		enabled: isVimeo && !!vimeoId,
	});

	// ============================================
	// Event Handlers
	// ============================================

	// Defensive check before dispatch
	const safeUpdatePlaybackState = useCallback((state: "playing" | "paused" | "none") => {
		if (vidPlayerRef.current) {
			updatePlaybackState(state);
		}
	}, [updatePlaybackState]);

	// ============================================
	// iOS Audio Session (interruption handling)
	// ============================================

	const wasPlayingBeforeInterruption = useRef(false);

	useAudioSession(
		{
			onInterruptionBegan: () => {
				console.log("[VideoPlayer] iOS interruption began - pausing");
				wasPlayingBeforeInterruption.current = vidPlayerRef.current?.paused === false;
				vidPlayerRef.current?.pause().catch(console.error);
				safeUpdatePlaybackState("paused");
			},
		onInterruptionEnded: () => {
			console.log("[VideoPlayer] iOS interruption ended");
			if (wasPlayingBeforeInterruption.current) {
					vidPlayerRef.current?.play().catch(console.error);
					safeUpdatePlaybackState("playing");
				}
				wasPlayingBeforeInterruption.current = false;
			},
			onHeadphonesDisconnected: () => {
				console.log("[VideoPlayer] Headphones disconnected - pausing");
				vidPlayerRef.current?.pause().catch(console.error);
				safeUpdatePlaybackState("paused");
			},
		},
		open === "VIDEO" && platform === "ios"
	);

	const handlePlay = useCallback(
		async (event: MediaPlayEvent): Promise<void> => {
			console.log("onPlay", event);
			safeUpdatePlaybackState("playing");
			engineHandlePlay();

			try {
				await markAsCompleted(productId, courseClassesId);
				await addOwnProduct();
			} catch (error) {
				console.log("error", error);
			}
		},
		[
			markAsCompleted,
			productId,
			courseClassesId,
			addOwnProduct,
			safeUpdatePlaybackState,
			engineHandlePlay,
		]
	);

	const handlePause = useCallback(() => {
		safeUpdatePlaybackState("paused");
		engineHandlePause();
	}, [safeUpdatePlaybackState, engineHandlePause]);

	const handleTimeUpdate = useCallback(
		async (event: { currentTime: number }) => {
			const currentTime = event.currentTime;
			const duration = vidPlayerRef.current?.duration ?? 0;

			if (duration > 0) {
				await saveVideoProgress(
					productId,
					courseClassesId,
					currentTime,
					duration
				);
			}

			engineHandleTimeUpdate();
			updatePositionState();
		},
		[saveVideoProgress, productId, courseClassesId, engineHandleTimeUpdate, updatePositionState]
	);

	const handleLoadedMetadata = useCallback(async () => {
		console.log("📊 Video metadata loaded");
		const player = vidPlayerRef.current;
		if (player) {
			try {
				const duration = await waitForDuration(player);
				console.log("✅ Duration ready:", duration);

				const resumeTime = shouldResumeFromSavedPosition(
					productId,
					courseClassesId,
					duration
				);

				if (resumeTime !== null) {
					console.log(`🎬 Resuming video from ${Math.round(resumeTime)}s`);
					player.currentTime = resumeTime;
				}

				handleCanPlay();
			} catch (error) {
				console.error("❌ Error waiting for duration:", error);
			}
		}
	}, [shouldResumeFromSavedPosition, productId, courseClassesId, handleCanPlay]);

	const handleEnded = useCallback(async () => {
		safeUpdatePlaybackState("none");
		engineHandleEnded();
		await clearVideoProgress(productId, courseClassesId);
	}, [clearVideoProgress, productId, courseClassesId, safeUpdatePlaybackState, engineHandleEnded]);

	const handlePlayerError = useCallback(async (error: unknown) => {
		console.error("Video player error:", error);
		const errorMessage = "Nie udało się załadować wideo. Spróbuj ponownie.";
		setVideoError(errorMessage);
		engineHandleError(error);

		Sentry.captureException(error, {
			tags: { component: 'VideoPlayer' },
			contexts: {
				video: {
					source: videoContent.video_source,
					productId,
					courseClassesId
				}
			}
		});

		await Toast.show({
			text: "Błąd ładowania wideo",
			duration: "long",
			position: "bottom"
		});
	}, [videoContent, productId, courseClassesId, engineHandleError]);

	// Safe wrapper functions for event handlers
	const safeHandlePlay = useCallback(
		async (event?: MediaPlayEvent): Promise<void> => {
			if (!event) {
				console.warn("[VideoPlayer] onPlay called with undefined event");
				return;
			}
			await handlePlay(event);
		},
		[handlePlay]
	);

	const safeHandleTimeUpdate = useCallback(
		async (event?: { currentTime: number }): Promise<void> => {
			if (!event || typeof event.currentTime !== 'number') {
				console.warn("[VideoPlayer] onTimeUpdate called with invalid event:", event);
				return;
			}
			await handleTimeUpdate(event);
		},
		[handleTimeUpdate]
	);

	const safeHandlePlayerError = useCallback(
		async (error?: unknown): Promise<void> => {
			if (!error) {
				console.warn("[VideoPlayer] onError called with undefined error");
				return;
			}
			await handlePlayerError(error);
		},
		[handlePlayerError]
	);

	// ============================================
	// Check for local video file
	// ============================================

	// Cleanup blob URL on unmount
	useEffect(() => {
		return (): void => {
			if (localVideoBlobUrl && localVideoBlobUrl.startsWith('blob:')) {
				URL.revokeObjectURL(localVideoBlobUrl);
			}
		};
	}, [localVideoBlobUrl]);

	useEffect(() => {
		const checkLocalVideo = async (): Promise<void> => {
			if (open === "VIDEO") {
				console.log("🔍 [VideoPlayer] Checking local video", {
					productType,
					productId,
					courseClassesId,
					videoContent,
					isNativePlatform: isNativePlatform(),
					platform,
				});
				try {
					console.log("🔍 [VideoPlayer] Getting local video URI");
					const videoUri = await getLocalVideoUri(productType, productId, courseClassesId);
					console.log("🔍 [VideoPlayer] Video URI result:", videoUri);
					setLocalVideoBlobUrl(videoUri);

					if (videoUri) {
						const videoId = generateVideoId(productType, productId, courseClassesId);
						await updateLastAccessed(videoId);
						console.log("📂 [VideoPlayer] Playing from local storage - SUCCESS");
					} else {
						console.log("📂 [VideoPlayer] No local video found");

						if (videoContent.video_source === "local_memory") {
							setVideoError("Wideo nie zostało znalezione w pamięci lokalnej. Spróbuj pobrać je ponownie.");
						}
					}
				} catch (error) {
					console.error("❌ [VideoPlayer] Error loading local video:", error);
					setLocalVideoBlobUrl(null);

					if (videoContent.video_source === "local_memory") {
						setVideoError("Błąd podczas ładowania wideo z pamięci lokalnej.");
					}
				}
			}
		};
		checkLocalVideo().catch((error) => {
			console.error("❌ [VideoPlayer] Error in checkLocalVideo:", error);
			setLocalVideoBlobUrl(null);
		});
	}, [productId, productType, courseClassesId, open, videoContent, platform]);

	// ============================================
	// Determine media source
	// ============================================

	const getMediaSource = useCallback(() => {
		if (localVideoBlobUrl) {
			return createLocalSource(localVideoBlobUrl, "video");
		}
		if (videoContent.video_source === "akademia_source") {
			return createMediaSource(videoContent.videoUrl, { isLocal: false });
		}
		if (vimeoVideoLink?.play?.hls?.link) {
			return createHlsSource(vimeoVideoLink.play.hls.link);
		}
		return null;
	}, [localVideoBlobUrl, videoContent, vimeoVideoLink?.play?.hls?.link]);

	// Common player props
	const commonPlayerProps = {
		controls: true,
		playsInline: true,
		className: cn("w-full h-full", open === "VIDEO" ? "block" : "!hidden"),
		load: "visible" as const,
		preload: "auto" as const,
		ref: vidPlayerRef,
		streamType: "on-demand" as const,
		onPlay: safeHandlePlay,
		onPause: handlePause,
		onTimeUpdate: safeHandleTimeUpdate,
		onEnded: handleEnded,
		onError: safeHandlePlayerError,
		onCanPlay: handleCanPlay,
	};

	// Debug: Log which player will be rendered
	// console.log("🎬 [VideoPlayer] Rendering decision:", {
	// 	videoContent,
	// 	localVideoBlobUrl,
	// 	vimeoVideoLink: vimeoVideoLink?.play?.hls?.link,
	// 	platform,
	// 	mediaSource: getMediaSource(),
	// 	willRenderAkademia: videoContent.video_source === "akademia_source",
	// 	willRenderVimeoHLS: vimeoVideoLink?.play?.hls?.link && !localVideoBlobUrl,
	// 	willRenderVimeoLocal: localVideoBlobUrl && videoContent.video_source === "vimeo_source"
	// });

	return (
		<>
			<button
				className="absolute top-6 right-6 p-4 text-white text-[40px] z-50 font-extrabold"
				onClick={async () => {
					await vidPlayerRef.current?.pause();
					safeUpdatePlaybackState("none");
					setOpen(false);
				}}
			>
				✕
			</button>
			{videoContent.video_source === "akademia_source" && (
				<MediaPlayer
					{...commonPlayerProps}
					src={localVideoBlobUrl || videoContent.videoUrl}
					onLoadedMetadata={handleLoadedMetadata}
				>
					<MediaProvider />
				</MediaPlayer>
			)}

			{/* Only load HLS for Vimeo if NO local video */}
			{vimeoVideoLink?.play?.hls?.link && !localVideoBlobUrl && (
				<MediaPlayer
					{...commonPlayerProps}
					src={{
						src: vimeoVideoLink.play.hls.link,
						type: "application/x-mpegurl",
					}}
					onLoadedData={() => {
						const player = vidPlayerRef.current;
						if (player && vimeoVideoLink?.play?.hls?.link) {
							const resumeTime = shouldResumeFromSavedPosition(
								productId,
								courseClassesId,
								player.duration
							);

							if (resumeTime !== null) {
								console.log(`🎬 Resuming video from ${Math.round(resumeTime)}s`);
								player.currentTime = resumeTime;
							}
						}
						handleCanPlay();
					}}
				>
					<MediaProvider />
				</MediaPlayer>
			)}

			{/* Local video for Vimeo source */}
			{localVideoBlobUrl && videoContent.video_source === "vimeo_source" && (
				<MediaPlayer
					{...commonPlayerProps}
					src={localVideoBlobUrl}
					onLoadedMetadata={handleLoadedMetadata}
				>
					<MediaProvider />
				</MediaPlayer>
			)}

			{/* Local memory video - ALWAYS use local file */}
			{videoContent.video_source === "local_memory" && (
				<>
					{localVideoBlobUrl ? (
						<MediaPlayer
							{...commonPlayerProps}
							src={{
								src: localVideoBlobUrl,
								type: "video/mp4"
							}}
							onLoadedMetadata={handleLoadedMetadata}
						>
							<MediaProvider />
						</MediaPlayer>
					) : (
						<div className="absolute inset-0 flex items-center justify-center bg-black text-white">
							<p>Ładowanie wideo z pamięci lokalnej...</p>
						</div>
					)}
				</>
			)}

			{/* Error UI */}
			{videoError && (
				<div className="absolute inset-0 flex items-center justify-center bg-black/80 z-40 p-6">
					<div className="bg-white rounded-lg p-6 max-w-md">
						<h3 className="text-lg font-semibold mb-2 text-red-600">
							Błąd odtwarzania wideo
						</h3>
						<p className="text-sm text-gray-700 mb-4">{videoError}</p>
						<div className="flex gap-3">
							<button
								onClick={() => {
									setVideoError(null);
									setOpen(false);
								}}
								className="flex-1 px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
							>
								Zamknij
							</button>
							<button
								onClick={() => {
									setVideoError(null);
									window.location.reload();
								}}
								className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
							>
								Odśwież stronę
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default VideoPlayer;
